import React, {FC, useState} from 'react';
import {
  Alert,
  IconButton,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import {useMutation} from '@apollo/client';
import {useTranslation} from 'react-i18next';
import {
  CREATE_PALOWAN_VACATION_ASSIGNMENT,
  UPDATE_PALOWAN_VACATION_ASSIGNMENT,
} from 'graphql/palowan-vacation/Mutations';
import {PalowanVacationAssignment} from 'models/PalowanVacationAssignment';

/** Sentinel value used to identify the in-progress new row state. */
const NEW_ROW_ID = '__new__';

/** Number of skeleton rows to render while loading. */
const SKELETON_ROW_COUNT = 3;

/** Column definitions for the inline range table. */
const COLUMNS = [
  {
    id: 'range',
    labelKey: 'leaves.PalowanRangeInlineTable.columns.range',
    align: 'left' as const,
  },
  {
    id: 'days',
    labelKey: 'leaves.PalowanRangeInlineTable.columns.days',
    align: 'right' as const,
  },
  {
    id: 'actions',
    labelKey: 'leaves.PalowanRangeInlineTable.columns.actions',
    align: 'center' as const,
  },
];

export interface PalowanRangeInlineTableProps {
  /** Current vacation assignment ranges for this palowan. */
  assignments: PalowanVacationAssignment[];
  /** When true, show skeleton rows instead of real data. */
  loading: boolean;
  /** When false, edit/create actions are disabled (country inactive). */
  isCountryActive: boolean;
  /** ID of the palowan these ranges belong to. */
  palowanId: string;
  /** When true, render the empty new-row form at the end of the table. */
  isAddingNewRow: boolean;
  /** Called when the disable action is triggered on an active row. */
  onDisable: (assignment: PalowanVacationAssignment) => void;
  /** Called when the enable action is triggered on an inactive row. */
  onEnable: (assignment: PalowanVacationAssignment) => void;
  /** Called after a successful create or update mutation to refresh parent data. */
  onMutationSuccess: () => void;
  /** Called when the new row is cancelled so the parent can reset its state. */
  onCancelNewRow: () => void;
}

/** Form field values shared by both the new row and the edit row. */
interface RowFormValues {
  seniorityFromYears: string;
  seniorityToYears: string;
  vacationDays: string;
}

const EMPTY_FORM: RowFormValues = {
  seniorityFromYears: '',
  seniorityToYears: '',
  vacationDays: '',
};

/**
 * Extracts the first human-readable message from an Apollo GraphQL error.
 * Prefers the first graphQLErrors message (set by the backend) over the
 * top-level network/wrapper message.
 * Returns null when no useful message can be found.
 */
const extractBackendMessage = (err: unknown): string | null => {
  const apolloErr = err as {
    message?: string;
    graphQLErrors?: Array<{message: string}>;
  };
  return apolloErr?.graphQLErrors?.[0]?.message ?? null;
};

/**
 * Returns true when the Apollo error indicates a 409 duplicate conflict.
 * Checks both graphQLErrors extension codes and the error message text.
 */
const isDuplicateError = (err: unknown): boolean => {
  const apolloErr = err as {
    message?: string;
    graphQLErrors?: Array<{message: string; extensions?: {code?: string}}>;
  };
  const code = apolloErr?.graphQLErrors?.[0]?.extensions?.code ?? '';
  const msg =
    apolloErr?.graphQLErrors?.[0]?.message ?? apolloErr?.message ?? '';
  return (
    code === '409' ||
    code === 'CONFLICT' ||
    msg.includes('409') ||
    msg.toLowerCase().includes('conflict') ||
    msg.toLowerCase().includes('duplicate') ||
    msg.toLowerCase().includes('already exists')
  );
};

/**
 * Inline table that displays and manages vacation seniority ranges for a single palowan.
 * Supports three row states: read (display only), edit (inline TextFields), and new (empty row).
 * Only one row can be in edit/new mode at a time.
 *
 * @param assignments - Current ranges to render.
 * @param loading - Renders skeletons when true.
 * @param isCountryActive - When false, all write actions are disabled.
 * @param palowanId - ID passed to create mutations.
 * @param isAddingNewRow - Controlled flag from the parent to show/hide the new-row form.
 * @param onDisable - Opens the disable confirmation modal.
 * @param onEnable - Opens the enable confirmation modal.
 * @param onMutationSuccess - Refresh callback invoked after a successful mutation.
 * @param onCancelNewRow - Informs the parent that the new-row form was dismissed.
 * @returns A MUI Table with inline editing support.
 */
const PalowanRangeInlineTable: FC<PalowanRangeInlineTableProps> = ({
  assignments,
  loading,
  isCountryActive,
  palowanId,
  isAddingNewRow,
  onDisable,
  onEnable,
  onMutationSuccess,
  onCancelNewRow,
}) => {
  const {t} = useTranslation();

  // null = no row editing, '__new__' = new row form, UUID = editing that row
  const [editingRowId, setEditingRowId] = useState<string | null>(null);
  const [formValues, setFormValues] = useState<RowFormValues>(EMPTY_FORM);
  const [rowError, setRowError] = useState<string | null>(null);

  const [createAssignment, {loading: isCreating}] = useMutation(
    CREATE_PALOWAN_VACATION_ASSIGNMENT
  );
  const [updateAssignment, {loading: isUpdating}] = useMutation(
    UPDATE_PALOWAN_VACATION_ASSIGNMENT
  );

  const isSaving = isCreating || isUpdating;

  /** True when all three required fields contain a non-empty value. */
  const isFormValid =
    formValues.seniorityFromYears.trim() !== '' &&
    formValues.seniorityToYears.trim() !== '' &&
    formValues.vacationDays.trim() !== '';

  /** Activates edit mode for an existing row, cancelling any in-progress row. */
  const handleEditRow = (assignment: PalowanVacationAssignment) => {
    setRowError(null);
    setEditingRowId(assignment.id);
    setFormValues({
      seniorityFromYears: String(assignment.seniorityFromYears),
      seniorityToYears: String(assignment.seniorityToYears),
      vacationDays: String(assignment.vacationDays),
    });
  };

  /** Cancels edit mode and clears form state. */
  const handleCancelEdit = () => {
    setEditingRowId(null);
    setFormValues(EMPTY_FORM);
    setRowError(null);
  };

  /** Cancels the new-row form and informs the parent. */
  const handleCancelNew = () => {
    setEditingRowId(null);
    setFormValues(EMPTY_FORM);
    setRowError(null);
    onCancelNewRow();
  };

  const handleFieldChange = (field: keyof RowFormValues, value: string) => {
    setFormValues(prev => ({...prev, [field]: value}));
  };

  /** Saves an updated existing row via the UPDATE mutation. */
  const handleSaveEdit = async (id: string) => {
    setRowError(null);
    try {
      await updateAssignment({
        variables: {
          id,
          seniorityFromYears: parseInt(formValues.seniorityFromYears, 10),
          seniorityToYears: parseInt(formValues.seniorityToYears, 10),
          vacationDays: parseInt(formValues.vacationDays, 10),
        },
      });
      setEditingRowId(null);
      setFormValues(EMPTY_FORM);
      onMutationSuccess();
    } catch (err) {
      if (isDuplicateError(err)) {
        setRowError(t('leaves.PalowanRangeInlineTable.duplicateError'));
      } else {
        setRowError(
          extractBackendMessage(err) ??
            t('leaves.PalowanRangeInlineTable.saveError')
        );
      }
    }
  };

  /** Saves the new row via the CREATE mutation. */
  const handleSaveNew = async () => {
    setRowError(null);
    try {
      await createAssignment({
        variables: {
          palowanId,
          seniorityFromYears: parseInt(formValues.seniorityFromYears, 10),
          seniorityToYears: parseInt(formValues.seniorityToYears, 10),
          vacationDays: parseInt(formValues.vacationDays, 10),
        },
      });
      setEditingRowId(null);
      setFormValues(EMPTY_FORM);
      onMutationSuccess();
      onCancelNewRow();
    } catch (err) {
      if (isDuplicateError(err)) {
        setRowError(t('leaves.PalowanRangeInlineTable.duplicateError'));
      } else {
        setRowError(
          extractBackendMessage(err) ??
            t('leaves.PalowanRangeInlineTable.saveError')
        );
      }
    }
  };

  /** Ref attached to the new-row TableRow to allow programmatic scroll-into-view. */
  const newRowRef = React.useRef<HTMLTableRowElement>(null);
  /** Ref attached to the "From" input in the new row to allow programmatic focus. */
  const fromInputRef = React.useRef<HTMLInputElement>(null);

  // Keep the editingRowId in sync when the parent activates the new row
  // (only if no row is currently being edited)
  React.useEffect(() => {
    if (isAddingNewRow && editingRowId === null) {
      setEditingRowId(NEW_ROW_ID);
      setFormValues(EMPTY_FORM);
      setRowError(null);
    }
    // If the parent removed the new row flag, cancel if we were in new-row mode
    if (!isAddingNewRow && editingRowId === NEW_ROW_ID) {
      setEditingRowId(null);
      setFormValues(EMPTY_FORM);
      setRowError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAddingNewRow]);

  // Scroll the scrollable container to its very bottom when the new row
  // appears, so the layout feels clean and no partial content is left visible.
  React.useEffect(() => {
    if (!isAddingNewRow || !newRowRef.current) return;
    // Walk up the DOM to find the nearest element that is actually scrollable
    // (has overflow auto/scroll AND has content taller than its visible area).
    // Skips containers like MUI TableContainer that have overflow:auto but no
    // fixed height and therefore are never really scrollable.
    let el: HTMLElement | null = newRowRef.current.parentElement;
    while (el) {
      const {overflowY} = window.getComputedStyle(el);
      const isScrollable =
        (overflowY === 'auto' || overflowY === 'scroll') &&
        el.scrollHeight > el.clientHeight;
      if (isScrollable) {
        el.scrollTo({top: el.scrollHeight, behavior: 'smooth'});
        break;
      }
      el = el.parentElement;
    }
    // Place focus on the "From" field so the user can start typing immediately.
    // preventScroll avoids the native browser scroll-into-view that would
    // conflict with and cancel the smooth scroll animation above.
    fromInputRef.current?.focus({preventScroll: true});
  }, [isAddingNewRow]);

  const renderSkeletonRows = () =>
    Array.from({length: SKELETON_ROW_COUNT}).map((_, index) => (
      <TableRow key={`skeleton-${index}`}>
        {COLUMNS.map(col => (
          <TableCell key={col.id}>
            <Skeleton variant="text" width="80%" />
          </TableCell>
        ))}
      </TableRow>
    ));

  const renderEmptyState = () => (
    <TableRow>
      <TableCell colSpan={COLUMNS.length} align="center">
        <Typography variant="body2" color="text.secondary" className="py-4">
          {t('leaves.PalowanRangeInlineTable.emptyState')}
        </Typography>
      </TableCell>
    </TableRow>
  );

  /** Renders the inline text fields shared by both edit and new rows. */
  const renderFormFields = (isNewRow = false) => (
    <>
      <TableCell>
        <div className="flex gap-2">
          <TextField
            size="small"
            label={t('leaves.PalowanRangeInlineTable.fieldFrom')}
            type="number"
            required
            value={formValues.seniorityFromYears}
            onChange={e =>
              handleFieldChange('seniorityFromYears', e.target.value)
            }
            inputProps={{'aria-label': 'Seniority from years'}}
            inputRef={isNewRow ? fromInputRef : undefined}
            disabled={isSaving}
            sx={{width: 80}}
          />
          <TextField
            size="small"
            label={t('leaves.PalowanRangeInlineTable.fieldTo')}
            type="number"
            required
            value={formValues.seniorityToYears}
            onChange={e =>
              handleFieldChange('seniorityToYears', e.target.value)
            }
            inputProps={{'aria-label': 'Seniority to years'}}
            disabled={isSaving}
            sx={{width: 80}}
          />
        </div>
      </TableCell>
      <TableCell align="right">
        <TextField
          size="small"
          label={t('leaves.PalowanRangeInlineTable.fieldDays')}
          type="number"
          required
          value={formValues.vacationDays}
          onChange={e => handleFieldChange('vacationDays', e.target.value)}
          inputProps={{'aria-label': 'Vacation days'}}
          disabled={isSaving}
          sx={{width: 80}}
        />
      </TableCell>
    </>
  );

  const renderDataRows = () =>
    assignments.map(assignment => {
      const isEditing = editingRowId === assignment.id;

      if (isEditing) {
        return (
          <TableRow key={assignment.id}>
            {renderFormFields()}
            <TableCell align="center">
              <IconButton
                aria-label="Save changes"
                onClick={() => handleSaveEdit(assignment.id)}
                disabled={isSaving || !isFormValid}
              >
                <SaveIcon />
              </IconButton>
              <IconButton
                aria-label="Cancel edit"
                onClick={handleCancelEdit}
                disabled={isSaving}
              >
                <CancelIcon />
              </IconButton>
            </TableCell>
          </TableRow>
        );
      }

      return (
        <TableRow
          key={assignment.id}
          hover
          sx={
            assignment.isActive
              ? {}
              : {opacity: 0.5, backgroundColor: '#f5f5f5'}
          }
        >
          <TableCell style={{wordBreak: 'break-word'}}>
            {t('leaves.PalowanRangeInlineTable.rangeDisplay', {
              from: assignment.seniorityFromYears,
              to: assignment.seniorityToYears,
            })}
          </TableCell>
          <TableCell align="right" style={{wordBreak: 'break-word'}}>
            {assignment.effectiveVacationDays}
          </TableCell>
          <TableCell align="center">
            {/* Edit button — disabled for inactive rows or when country is inactive */}
            {assignment.isActive && isCountryActive ? (
              <IconButton
                aria-label="Edit range"
                onClick={() => handleEditRow(assignment)}
              >
                <EditIcon />
              </IconButton>
            ) : (
              <Tooltip
                title={t('leaves.PalowanRangeInlineTable.tooltipEditDisabled')}
              >
                <span>
                  <IconButton aria-label="Edit range" disabled>
                    <EditIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {/* Disable / Enable toggle */}
            {assignment.isActive ? (
              <IconButton
                color="success"
                aria-label="Disable range"
                onClick={() => onDisable(assignment)}
                disabled={!isCountryActive}
              >
                <ToggleOffIcon />
              </IconButton>
            ) : (
              <IconButton
                color="error"
                aria-label="Enable range"
                onClick={() => onEnable(assignment)}
                disabled={!isCountryActive}
              >
                <ToggleOnIcon />
              </IconButton>
            )}
          </TableCell>
        </TableRow>
      );
    });

  const renderNewRow = () => (
    <TableRow key={NEW_ROW_ID} ref={newRowRef}>
      {renderFormFields(true)}
      <TableCell align="center">
        <IconButton
          aria-label="Save new range"
          onClick={handleSaveNew}
          disabled={isSaving || !isFormValid}
        >
          <SaveIcon />
        </IconButton>
        <IconButton
          aria-label="Cancel new range"
          onClick={handleCancelNew}
          disabled={isSaving}
        >
          <CancelIcon />
        </IconButton>
      </TableCell>
    </TableRow>
  );

  return (
    <Paper sx={{width: '100%'}}>
      {rowError && (
        <Alert severity="error" onClose={() => setRowError(null)}>
          {rowError}
        </Alert>
      )}
      <TableContainer sx={{maxHeight: 380, overflowY: 'auto'}}>
        <Table aria-label="Palowan vacation ranges table">
          <TableHead>
            <TableRow>
              {COLUMNS.map(col => (
                <TableCell key={col.id} align={col.align}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {t(col.labelKey)}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading
              ? renderSkeletonRows()
              : assignments.length === 0 && !isAddingNewRow
              ? renderEmptyState()
              : renderDataRows()}
            {/* New row always appended at the end when the parent flag is set */}
            {isAddingNewRow && renderNewRow()}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default PalowanRangeInlineTable;
